import React, { useState, useRef, useEffect } from "react";
import { Camera, Upload, Trash2, User } from "lucide-react";
import { useI18nStore } from "@/stores/i18nStore";
import { i18nToast } from "@/utils/i18nToast";
import {
  uploadAvatar,
  deleteAvatar,
  getAvatarUrls,
} from "@/services/api/avatar.service";
import { CameraCaptureModal } from "./CameraCaptureModal";
import { ImageCropModal } from "./ImageCropModal";
import { PixelCrop } from "react-image-crop";
import { useAuthStore } from "@/stores/authStore";
import { log } from "@/lib/log";

export const AvatarUploadBox: React.FC = () => {
  const { t } = useI18nStore();
  const { user } = useAuthStore();
  const [isUploading, setIsUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing avatar on mount
  useEffect(() => {
    const loadAvatar = async () => {
      if (user?.keycloakId) {
        try {
          const response = await getAvatarUrls(user.keycloakId);
          if (response.success && response.data.avatarUrl) {
            setAvatarUrl(response.data.avatarUrl);
          }
        } catch (error: any) {
          // No avatar yet, that's fine
          log.debug("No avatar found for user");
        }
      }
    };

    loadAvatar();
  }, [user?.keycloakId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        i18nToast.error(t.invalidFileType || "Invalid file type");
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        i18nToast.error(t.fileTooLarge || "File too large (max 5MB)");
        return;
      }

      setSelectedFile(file);
      setShowCrop(true);
    }
  };

  const handleCameraCapture = (file: File) => {
    setSelectedFile(file);
    setShowCamera(false);
    setShowCrop(true);
  };

  const handleCropComplete = async (croppedFile: File, cropData: PixelCrop) => {
    setShowCrop(false);
    setIsUploading(true);

    try {
      const response = await uploadAvatar(croppedFile, cropData);

      if (response.success) {
        setAvatarUrl(response.data.avatarUrl);
        i18nToast.success(
          t.avatarUploadedSuccessfully || "Avatar uploaded successfully",
        );

        // Dispatch custom event for header to update
        window.dispatchEvent(
          new CustomEvent("avatarUpdated", {
            detail: { avatarUrl: response.data.thumbnailUrl },
          }),
        );
      }
    } catch (error: any) {
      console.error("Avatar upload error:", error);
      i18nToast.error(
        error.response?.data?.error?.message ||
          t.avatarUploadFailed ||
          "Failed to upload avatar",
      );
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t.confirmDeleteAvatar || "Delete avatar?")) {
      return;
    }

    setIsUploading(true);
    try {
      await deleteAvatar();
      setAvatarUrl(null);
      i18nToast.success(t.avatarDeletedSuccessfully || "Avatar deleted");

      // Dispatch custom event for header to update
      window.dispatchEvent(
        new CustomEvent("avatarUpdated", {
          detail: { avatarUrl: null },
        }),
      );
    } catch (error: any) {
      console.error("Avatar delete error:", error);
      i18nToast.error(
        error.response?.data?.error?.message ||
          t.avatarDeleteFailed ||
          "Failed to delete avatar",
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Get avatar URL from state
  const displayAvatarUrl = avatarUrl;

  return (
    <>
      <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-white/20">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t.profilePicture || "Profile Picture"}
        </h3>

        <div className="flex items-center gap-6">
          {/* Avatar Display */}
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center shadow-lg">
              {displayAvatarUrl ? (
                <img
                  src={displayAvatarUrl}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-16 h-16 text-gray-400" />
              )}
            </div>

            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Upload Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Upload className="w-5 h-5" />
              {t.uploadPhoto || "Upload Photo"}
            </button>

            <button
              onClick={() => setShowCamera(true)}
              disabled={isUploading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Camera className="w-5 h-5" />
              {t.takePhoto || "Take Photo"}
            </button>

            {displayAvatarUrl && (
              <button
                onClick={handleDelete}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-5 h-5" />
                {t.deletePhoto || "Delete"}
              </button>
            )}
          </div>
        </div>

        {/* File Size Info */}
        <p className="mt-4 text-sm text-gray-600">
          {t.maxFileSize ||
            "Max file size: 5MB. Supported formats: JPEG, PNG, WebP, GIF"}
        </p>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Modals */}
      {showCamera && (
        <CameraCaptureModal
          onCapture={handleCameraCapture}
          onCancel={() => setShowCamera(false)}
        />
      )}

      {showCrop && selectedFile && (
        <ImageCropModal
          imageFile={selectedFile}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setShowCrop(false);
            setSelectedFile(null);
          }}
        />
      )}
    </>
  );
};
